import type { MutableRefObject } from 'react'
import { postKnowledgeBase } from '../api/voice-bot-client'
import { buildInsuranceRecoveryInstruction } from './realtime-event-helpers'
import type { VoiceSessionData } from '../types'

// ============================================
// runInsuranceNoInfoRecovery — Wave 4 side-effect helper
// ============================================
// Lifted from VoiceBotPanel.handleDataChannelMessage's
// `response.output_audio_transcript.done` branch.
//
// When the assistant claims it has no info but the KB actually has
// data for the last user question, this helper:
//   1. Re-runs the KB search for that exact question.
//   2. Builds a short instruction citing the recovered facts.
//   3. Sends it as a system-style user message and immediately
//      triggers a new response so the bot answers properly.
//
// The caller is responsible for the gating decision
// (`shouldRecoverInsuranceNoInfo`) and for marking the recovery as
// attempted (`insuranceRecoveryForMessageRef.current = userQuestion`)
// BEFORE invoking this helper, so we never double-fire for the same
// user message.
//
// Behavior preserved 1:1 with the original inline IIFE.

export interface RunInsuranceNoInfoRecoveryOptions {
  userQuestion: string
  publicKey?: string
  sessionDataRef: MutableRefObject<VoiceSessionData | null>
  dataChannelRef: MutableRefObject<RTCDataChannel | null>
  triggerResponse: (reason: string, immediate?: boolean) => void
}

export const runInsuranceNoInfoRecovery = async (
  opts: RunInsuranceNoInfoRecoveryOptions
): Promise<void> => {
  const {
    userQuestion,
    publicKey,
    sessionDataRef,
    dataChannelRef,
    triggerResponse,
  } = opts
  try {
    console.warn('🔁 Insurance no-info recovery triggered for:', userQuestion)
    const recoveryResponse = await postKnowledgeBase(
      {
        query: userQuestion,
        customerId: sessionDataRef.current?.customerId,
      },
      publicKey
    )
    const recoveryData = await recoveryResponse.json()

    if (!recoveryData?.results || recoveryData.results.length === 0) {
      console.warn('⚠️ Insurance no-info recovery found no results')
      return
    }

    const recoveryInstruction = buildInsuranceRecoveryInstruction(
      userQuestion,
      recoveryData.results
    )

    if (dataChannelRef.current?.readyState === 'open') {
      dataChannelRef.current.send(
        JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [{ type: 'input_text', text: recoveryInstruction }],
          },
        })
      )
      triggerResponse('insurance-no-info-recovery', true)
    }
  } catch (recoveryErr) {
    console.error('❌ Insurance no-info recovery failed:', recoveryErr)
  }
}
